/**
 * GivingDashboard — confirmed versus pending income, per church and campaign.
 *
 * Confirmed is money that actually landed. Pending is promised: an unconfirmed
 * card session, an offline pledge nobody has receipted yet. Amounts are never
 * converted between currencies — each is shown as it was given.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, HandCoins, Clock, CheckCircle2, Church, Megaphone, Globe, Send, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchJson = async (path) => {
  const r = await fetch(`${API_URL}${path}`, { headers: authHeaders() });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Request failed');
  return r.json();
};

const money = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const RANGES = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '12 months' },
];

const AmountPair = ({ currencies = [], testId }) => (
  <div className="space-y-1" data-testid={testId}>
    {currencies.length === 0 && <span className="text-slate-300">—</span>}
    {currencies.map((c) => (
      <div key={c.code} className="flex items-baseline gap-2 text-sm">
        <span className="text-[10px] font-mono text-slate-400 w-8">{c.code}</span>
        <span className="font-semibold text-emerald-700">{money(c.confirmed)}</span>
        {c.pending > 0 && (
          <span className="text-amber-600 text-xs">+{money(c.pending)} pending</span>
        )}
      </div>
    ))}
  </div>
);

const TotalCard = ({ code, confirmed, pending }) => {
  const share = confirmed + pending ? Math.round((confirmed / (confirmed + pending)) * 100) : 0;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`giving-total-${code}`}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400">{code} income</p>
      <p className="text-3xl font-semibold text-slate-900 mt-1">{money(confirmed)}</p>
      <div className="flex items-center gap-3 mt-2 text-xs">
        <span className="inline-flex items-center gap-1 text-emerald-700">
          <CheckCircle2 className="h-3 w-3" /> confirmed
        </span>
        <span className="inline-flex items-center gap-1 text-amber-600">
          <Clock className="h-3 w-3" /> {money(pending)} pending
        </span>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-amber-100 overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${share}%` }} />
      </div>
      <p className="text-[10px] text-slate-400 mt-1">{share}% of promised income has landed</p>
    </div>
  );
};

const Section = ({ icon: Icon, title, description, children, testId }) => (
  <Card data-testid={testId}>
    <CardHeader>
      <CardTitle className="text-lg flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-400" /> {title}
      </CardTitle>
      {description && <CardDescription>{description}</CardDescription>}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

export default function GivingDashboard() {
  const [days, setDays] = useState(90);
  const { data, isLoading, error } = useQuery({
    queryKey: ['giving-overview', days],
    queryFn: () => fetchJson(`/api/admin/giving/overview?days=${days}`),
    retry: false,
  });
  const qc = useQueryClient();
  const { data: nudgeQueue } = useQuery({
    queryKey: ['giving-nudge-queue'],
    queryFn: () => fetchJson('/api/admin/giving/nudges/queue'),
    retry: false,
  });
  const nudge = useMutation({
    mutationFn: () => fetch(`${API_URL}/api/admin/giving/nudges/run`, {
      method: 'POST', headers: authHeaders(),
    }).then(async (r) => {
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.detail || 'Could not send reminders');
      return b;
    }),
    onSuccess: (res) => {
      toast.success(res.sent
        ? `${res.sent} reminder${res.sent === 1 ? '' : 's'} sent with payment details`
        : 'Nothing was due a reminder');
      qc.invalidateQueries({ queryKey: ['giving-nudge-queue'] });
    },
    onError: (e) => toast.error(e.message),
  });
  const { data: pendingData } = useQuery({
    queryKey: ['giving-pending'],
    queryFn: () => fetchJson('/api/admin/giving/pending?limit=25'),
    retry: false,
  });
  // iter 623 — a gift given on the public church page is an intent until an
  // office says the money arrived; this is the only screen that can say so.
  const confirmGift = useMutation({
    mutationFn: (id) => fetch(`${API_URL}/api/admin/giving/records/${id}/confirm`, {
      method: 'POST', headers: authHeaders(),
    }).then(async (r) => {
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.detail || 'Could not confirm the gift');
      return b;
    }),
    onSuccess: (res) => {
      toast.success(res.already_confirmed
        ? 'Already confirmed by someone else'
        : 'Gift confirmed — it is now recorded income');
      qc.invalidateQueries({ queryKey: ['giving-pending'] });
      qc.invalidateQueries({ queryKey: ['giving-overview'] });
    },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div className="py-24 text-center text-slate-400" data-testid="giving-dashboard-loading">
        <Loader2 className="h-5 w-5 animate-spin mx-auto mb-3" />
        Adding up the giving…
      </div>
    );
  }
  if (error) {
    return (
      <div className="py-24 text-center text-rose-600" data-testid="giving-dashboard-error">
        {error.message}
      </div>
    );
  }

  const totals = data?.totals || [];
  const gifts = pendingData?.gifts || [];

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto space-y-4 sm:space-y-6" data-testid="giving-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <HandCoins className="h-5 w-5 text-emerald-600" /> Giving
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            What has landed versus what is still promised
            {data?.scope?.global
              ? ' · across the whole ministry'
              : ` · ${data?.scope?.church_count ?? 0} churches in your care`}
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {RANGES.map((r) => (
            <Button
              key={r.days}
              size="sm"
              variant={days === r.days ? 'default' : 'ghost'}
              className="h-7 text-xs"
              data-testid={`giving-range-${r.days}`}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </Button>
          ))}
        </div>
      </div>

      <a
        href="/admin/giving/identity-requests"
        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-900"
        data-testid="giving-identity-requests-link"
      >
        <EyeOff className="h-3.5 w-3.5" />
        Givers are shown by coded reference above assembly level — request a name
      </a>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="giving-totals">
        {totals.length === 0 && (
          <p className="text-sm text-slate-400" data-testid="giving-totals-empty">
            No giving recorded in this period yet.
          </p>
        )}
        {totals.map((t) => <TotalCard key={t.code} {...t} />)}
      </div>

      <Section icon={Church} title="By church" testId="giving-by-church"
               description="Offerings that have been receipted, plus card gifts still awaiting confirmation">
        {(data?.churches || []).length === 0 && (
          <p className="py-6 text-center text-sm text-slate-400" data-testid="giving-churches-empty">Nothing recorded yet</p>
        )}
        <ul className="divide-y divide-slate-100">
          {(data?.churches || []).map((c) => (
            <li key={c.church_id}
                className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                data-testid={`giving-church-${c.church_id}`}>
              <div className="min-w-0">
                <p className="font-medium text-slate-800 text-sm break-words">{c.name}</p>
                <p className="text-[11px] text-slate-400 capitalize">
                  {(c.level || '—').replace(/_/g, ' ')}{c.city ? ` · ${c.city}` : ''}
                </p>
              </div>
              <AmountPair currencies={c.currencies} testId={`giving-church-amounts-${c.church_id}`} />
            </li>
          ))}
        </ul>
      </Section>

      <Section icon={Megaphone} title="By campaign" testId="giving-by-campaign"
               description="Pledges count as pending until the payment is confirmed">
        {(data?.campaigns || []).length === 0 && (
          <p className="py-6 text-center text-sm text-slate-400" data-testid="giving-campaigns-empty">No campaign giving yet</p>
        )}
        <ul className="divide-y divide-slate-100">
          {(data?.campaigns || []).map((c) => (
            <li key={c.campaign_id}
                className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                data-testid={`giving-campaign-${c.campaign_id}`}>
              <div className="min-w-0">
                <p className="font-medium text-slate-800 text-sm break-words">{c.name}</p>
                {c.target_amount ? (
                  <p className="text-[11px] text-slate-400">Target {money(c.target_amount)}</p>
                ) : null}
              </div>
              <AmountPair currencies={c.currencies} testId={`giving-campaign-amounts-${c.campaign_id}`} />
            </li>
          ))}
        </ul>
      </Section>

      {data?.scope?.global && (
        <Section icon={Globe} title="Units and the donation portal" testId="giving-platform-wide"
                 description="Online giving recorded against organisation units, and the public donation portal">
          <div className="space-y-4">
            {(data?.units || []).map((u) => (
              <div key={u.unit_id || u.name} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 border-b border-slate-100 pb-2"
                   data-testid={`giving-unit-${u.unit_id || u.name}`}>
                <span className="text-sm text-slate-700">{u.name}</span>
                <AmountPair currencies={u.currencies} />
              </div>
            ))}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 pt-1" data-testid="giving-portal">
              <span className="text-sm font-medium text-slate-800">Donation portal</span>
              <AmountPair currencies={data?.portal?.currencies} />
            </div>
          </div>
        </Section>
      )}

      <Section icon={Clock} title="Waiting on confirmation" testId="giving-pending-list"
               description="Chase these, or confirm them once the money is in">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Button size="sm" className="h-8 text-xs" data-testid="send-nudges-btn"
                  disabled={nudge.isPending} onClick={() => nudge.mutate()}>
            {nudge.isPending ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Send className="h-3 w-3 mr-1.5" />}
            Send payment reminders
          </Button>
          <span className="text-[11px] text-slate-400" data-testid="nudge-due-count">
            {nudgeQueue?.sent ?? 0} pledge{(nudgeQueue?.sent ?? 0) === 1 ? '' : 's'} due a reminder
            {' '}· reminders go out automatically each morning
          </span>
        </div>
        {gifts.length === 0 && (
          <p className="py-6 text-center text-sm text-slate-400" data-testid="giving-pending-empty">
            Nothing outstanding — every gift is confirmed
          </p>
        )}
        <ul className="divide-y divide-slate-100">
          {gifts.map((g) => (
            <li key={g.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1"
                data-testid={`giving-pending-${g.id}`}>
              <div className="min-w-0">
                <p className="text-sm text-slate-800 break-words">{g.name}</p>
                <p className="text-[10px] font-mono text-slate-400 break-all">{g.reference || '—'}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className="text-[10px] capitalize">{g.source}</Badge>
                <span className="font-semibold text-amber-700 text-sm">{g.currency} {money(g.amount)}</span>
                {g.confirmable && (
                  <Button size="sm" variant="outline" className="h-7 text-xs"
                          data-testid={`confirm-gift-${g.id}`}
                          disabled={confirmGift.isPending}
                          onClick={() => confirmGift.mutate(g.id)}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Confirm
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}
